// buildQueryMatrix: {vertical, territory} → the search_queries rows a niche needs (plan §3).
//
// Deterministic on purpose. The owner sees exactly why a query exists (its dims), regenerating
// adds only what is missing, the whole thing runs in Vitest with no network, and it does not
// depend on a model being up — the only stage that creates leads must never wait on one.
//
// One deliberate deviation from the plan's §3.3: a Latin query is NOT folded into its Cyrillic
// twin when it equals the transliteration. Google answers "zabolekar Burgas" and "зъболекар
// Бургас" with different result sets — which is exactly why 0026 added the Latin spellings —
// so both are emitted. Only identical normalised texts collapse.
import { BIG_AREA_WEIGHT, BIG_AREA_YIELD_FACTOR, CORE_AREA_SYNONYMS, DEFAULT_TIER_COOLDOWNS, EN_TEMPLATES, SERVICE_AREA_SERVICES, TIERS, TIER_PRIORS, } from './templates.js';
/** NFC → lowercase → one space between words → no punctuation except hyphens. */
export function normalizeQuery(text) {
    return text
        .normalize('NFC')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
export function buildQueryMatrix(vertical, territory, options = {}) {
    const tiers = new Set(options.includeTiers ?? TIERS);
    const cooldowns = {
        ...DEFAULT_TIER_COOLDOWNS,
        ...options.tierCooldowns,
    };
    const areas = territory.areas.filter((area) => area.includeInQueries);
    const drafts = [];
    const add = (tier, draft) => {
        if (tiers.has(tier))
            drafts.push({ ...draft, tier });
    };
    for (const script of scriptsFor(vertical, territory)) {
        addScriptTiers(script, areas, add);
        if (script.lang === 'bg')
            addBulgarianTiers(script, areas, vertical.intentPhrasingsBg, add);
    }
    addEnglishTier(vertical, territory, add);
    return finish(drafts, cooldowns, options.existing ?? []);
}
function scriptsFor(vertical, territory) {
    const langs = new Set(vertical.searchLanguages);
    const scripts = [];
    if (langs.has('bg')) {
        scripts.push({
            lang: 'bg',
            synonyms: vertical.synonymsBg,
            services: vertical.servicesBg,
            city: territory.cityNameBg,
            areaName: (area) => area.name,
        });
    }
    if (langs.has('lat')) {
        scripts.push({
            lang: 'lat',
            synonyms: vertical.synonymsLat,
            services: vertical.servicesLat,
            city: territory.cityNameLat,
            areaName: (area) => area.nameLat || area.name,
        });
    }
    return scripts;
}
/** core, core_area and service — the tiers every script gets. */
function addScriptTiers(script, areas, add) {
    const { lang, city } = script;
    for (const syn of script.synonyms) {
        add('core', {
            queryText: `${syn} ${city}`,
            areaHint: null,
            lang,
            dims: { syn, lang },
            yieldFactor: 1,
        });
    }
    for (const syn of script.synonyms.slice(0, CORE_AREA_SYNONYMS)) {
        for (const area of areas) {
            add('core_area', {
                queryText: `${syn} ${city} ${script.areaName(area)}`,
                areaHint: area.name,
                lang,
                dims: { syn, area: area.name, lang },
                yieldFactor: isBig(area) ? BIG_AREA_YIELD_FACTOR : 1,
            });
        }
    }
    for (const service of script.services) {
        add('service', {
            queryText: `${service} ${city}`,
            areaHint: null,
            lang,
            dims: { service, lang },
            yieldFactor: 1,
        });
    }
}
/** service_area and intent exist in Bulgarian only: big districts and the phrasings that reorder Google. */
function addBulgarianTiers(script, areas, phrasings, add) {
    const bigAreas = areas.filter(isBig);
    for (const service of script.services.slice(0, SERVICE_AREA_SERVICES)) {
        for (const area of bigAreas) {
            add('service_area', {
                queryText: `${service} ${script.city} ${area.name}`,
                areaHint: area.name,
                lang: 'bg',
                dims: { service, area: area.name, lang: 'bg' },
                yieldFactor: BIG_AREA_YIELD_FACTOR,
            });
        }
    }
    const primary = script.synonyms[0];
    if (primary === undefined)
        return;
    for (const phrasing of phrasings) {
        add('intent', {
            queryText: phrasing.replace('{syn}', primary).replace('{city}', script.city),
            areaHint: null,
            lang: 'bg',
            dims: { syn: primary, phrasing, lang: 'bg' },
            yieldFactor: 1,
        });
    }
}
function addEnglishTier(vertical, territory, add) {
    if (!vertical.searchLanguages.includes('en'))
        return;
    const name = vertical.nameEn.trim().toLowerCase();
    if (name === '')
        return;
    for (const template of EN_TEMPLATES) {
        add('en', {
            queryText: template.replace('{name}', name).replace('{city}', territory.cityNameLat),
            areaHint: null,
            lang: 'en',
            dims: { template, lang: 'en' },
            yieldFactor: 1,
        });
    }
}
const isBig = (area) => area.searchWeight >= BIG_AREA_WEIGHT;
/** Dedupe, skip what exists, price, and order best-first. */
function finish(drafts, cooldowns, existing) {
    const taken = new Set(existing.map(normalizeQuery));
    const seen = new Set();
    const skippedExisting = [];
    let skippedDuplicate = 0;
    const candidates = [];
    for (const draft of drafts) {
        const text = normalizeQuery(draft.queryText);
        if (text === '')
            continue;
        if (seen.has(text)) {
            skippedDuplicate++;
            continue;
        }
        seen.add(text);
        if (taken.has(text)) {
            skippedExisting.push(draft.queryText.trim());
            continue;
        }
        candidates.push({
            queryText: draft.queryText.replace(/\s+/g, ' ').trim(),
            areaHint: draft.areaHint,
            lang: draft.lang,
            tier: draft.tier,
            dims: { ...draft.dims, tier: draft.tier },
            expectedYield: Math.min(100, Math.round(TIER_PRIORS[draft.tier] * draft.yieldFactor)),
            cooldownDays: cooldowns[draft.tier],
        });
    }
    const tierRank = new Map(TIERS.map((tier, index) => [tier, index]));
    candidates.sort((a, b) => b.expectedYield - a.expectedYield ||
        (tierRank.get(a.tier) ?? 0) - (tierRank.get(b.tier) ?? 0) ||
        a.queryText.localeCompare(b.queryText, 'bg'));
    const byTier = Object.fromEntries(TIERS.map((tier) => [tier, 0]));
    for (const candidate of candidates)
        byTier[candidate.tier]++;
    return { candidates, skippedExisting, skippedDuplicate, byTier };
}
//# sourceMappingURL=build.js.map